import { motion } from 'motion/react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
	data,
	Link,
	type LoaderFunctionArgs,
	useSearchParams,
} from 'react-router'
import MarketingSpacer from '#app/components/marketing/spacer.js'
import { Button } from '#app/components/ui/button.js'
import Image from '#app/components/ui/image.js'
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from '#app/components/ui/sheet.js'
import { Text } from '#app/components/ui/typography.tsx'
import { cachifiedWithMetrics } from '#app/utils/cache-metrics.server.ts'
import { BASE_URL } from '#app/utils/config.ts'
import { prisma } from '#app/utils/db.server.js'
import { LocalizedLink } from '#app/utils/i18n.tsx'
import {
	generateMetaTranslationLoader,
	i18next,
} from '#app/utils/i18next.server.js'
import { createPerformanceTimer } from '#app/utils/timing.server.ts'
import { generateTranslatedMeta } from '#app/utils/meta.ts'
import { cn, getMaterialDesignImgSrc } from '#app/utils/misc.js'
import routes from '#routes/index.js'
import { type Route } from '../../../../../.react-router/types/app/routes/($lang)+/_marketing+/products+/+types'
import { usePublicContext } from '../../__public-context'

export async function loader({ request }: LoaderFunctionArgs) {
	const timer = createPerformanceTimer()
	timer.start('total', 'Total loader execution')

	const lang = await i18next.getLocale(request)
	const searchParams = new URLSearchParams(request.url.split('?')[1])

	const search = searchParams.get('search')
	const colorQueryParams = searchParams.getAll('color')
	const typologyQueryParams = searchParams.getAll('typology')
	const widthQueryParams = searchParams.getAll('width')
	const finishQueryParams = searchParams.getAll('finish')
	const technologyParams = searchParams.getAll('technology')

	// Create cache key based on language and all filter params
	const cacheKey = `products-list:${lang}:${search || 'all'}:${colorQueryParams.sort().join(',')}:${typologyQueryParams.sort().join(',')}:${widthQueryParams.sort().join(',')}:${finishQueryParams.sort().join(',')}:${technologyParams.sort().join(',')}`

	// Fetch data with caching (30 min TTL for infrequent updates)
	const cachedData = await cachifiedWithMetrics({
		key: cacheKey,
		ttl: 1000 * 60 * 30, // 30 minutes
		route: 'products.index',
		async getFreshValue() {
			timer.start('db-collections', 'Fetch collections from database')
			const amountOfCollections = await prisma.collection.count({
				where: {
					published: true,
				},
			})

			const collections = await prisma.collection.findMany({
				orderBy: {
					name: 'asc',
				},
				include: {
					designsImages: {
						take: 1,
						orderBy: {
							order: 'asc',
						},
						select: {
							id: true,
							// Only select the ID - blob will be loaded on-demand via resource route
							// This drastically improves performance by not loading image data
						},
					},
					characteristics: {
						select: {
							new: true,
						},
					},
					translations: {
						where: {
							languageCode: lang as string,
						},
					},
				},
				where: {
					published: true,
					translations: search
						? {
								some: {
									collection: {
										name: {
											contains: search as string,
										},
									},
								},
							}
						: undefined,
					colors:
						colorQueryParams.length > 0
							? {
									some: {
										code: {
											in: colorQueryParams ?? [],
										},
									},
								}
							: undefined,
					typology:
						typologyQueryParams.length > 0
							? {
									code: {
										in: typologyQueryParams ?? [],
									},
								}
							: undefined,
					characteristics:
						technologyParams.length > 0
							? {
									fourthDimension: technologyParams.includes('4d'),
								}
							: undefined,

					variants:
						finishQueryParams.length > 0 || widthQueryParams.length > 0
							? {
									some: {
										OR: [
											{
												width: {
													width: {
														in:
															widthQueryParams.map((item) => Number(item)) ??
															[],
													},
												},
											},
											{
												finish: {
													code: {
														in: finishQueryParams ?? [],
													},
												},
											},
										],
									},
								}
							: undefined,
				},
			})
			timer.end('db-collections')

			timer.start('db-filters', 'Fetch filter options from database')
			const [color, typologies, width, finish] = await Promise.all([
				prisma.collectionColor.findMany({
					include: {
						translations: {
							where: {
								languageCode: lang as string,
							},
						},
					},
					where: {
						published: true,
						collections: {
							some: {
								published: true,
							},
						},
					},
				}),
				prisma.collectionTypology.findMany({
					include: {
						translations: {
							where: {
								languageCode: lang as string,
							},
						},
						_count: {
							select: {
								collections: {
									where: {
										published: true,
									},
								},
							},
						},
					},
					where: {
						collections: {
							some: {
								published: true,
							},
						},
					},
				}),
				prisma.collectionWidth.findMany({}),
				prisma.collectionFinish.findMany({
					include: {
						translations: {
							where: {
								languageCode: lang as string,
							},
						},
					},
					where: {
						variants: {
							some: {
								collection: {
									published: true,
								},
							},
						},
					},
				}),
			])
			timer.end('db-filters')

			return {
				amountOfCollections,
				collections: collections.sort(
					(a, b) =>
						Number(b.characteristics?.new) - Number(a.characteristics?.new),
				),
				typologies,
				filters: {
					color: color.reduce(
						(acc, color) => ({
							...acc,
							[color.code]: color.translations[0]?.name,
						}),
						{},
					),
					typology: typologies.reduce(
						(acc, typology) => ({
							...acc,
							[typology.code]: typology.translations[0]?.name,
						}),
						{},
					),
					width: width.reduce(
						(acc, width) => ({
							...acc,
							[width.width]: `${width.width} mm`,
						}),
						{},
					),
					finish: finish.reduce(
						(acc, finish) => ({
							...acc,
							[finish.code]: finish.translations[0]?.name,
						}),
						{},
					),
					technology: {
						'4d': '4D',
					},
				},
			}
		},
	})

	const meta = await generateMetaTranslationLoader({
		request,
		namespace: 'products',
		keys: {
			title: 'title',
			description: 'description',
			canonical: `${BASE_URL}${routes.public.products.list.route({})}`,
		},
	})

	timer.end('total')

	return data(
		{
			...cachedData,
			meta,
		},
		{
			headers: {
				'Server-Timing': timer.toServerTimingHeader(),
			},
		},
	)
}

export const meta = generateTranslatedMeta

function ProductFilterButton(props: React.ComponentPropsWithoutRef<'button'>) {
	return (
		<Button
			{...props}
			className={cn('font-light lowercase tracking-[0.2em]', props.className)}
			size="sm"
			variant="link"
		>
			{props.children}
		</Button>
	)
}

type FilterKey = 'color' | 'typology' | 'width' | 'finish' | 'technology'

function ProductFilterSheet({
	filterItems,
}: {
	filterItems: Record<FilterKey, Record<string, string>>
}) {
	const { t } = useTranslation('products')
	const [searchParams, setSearchParams] = useSearchParams()
	const [selected, setSelected] = useState<{
		selected: '' | FilterKey
		activeFilters: Record<string, string[]>
	}>({
		selected: '',
		activeFilters: {
			color: searchParams.getAll('color') ?? [],
			typology: searchParams.getAll('typology') ?? [],
			width: searchParams.getAll('width') ?? [],
			finish: searchParams.getAll('finish') ?? [],
			technology: searchParams.getAll('technology') ?? [],
		},
	})

	useEffect(() => {
		setSelected((prev) => ({
			...prev,
			activeFilters: {
				color: searchParams.getAll('color') ?? [],
				typology: searchParams.getAll('typology') ?? [],
				width: searchParams.getAll('width') ?? [],
				finish: searchParams.getAll('finish') ?? [],
				technology: searchParams.getAll('technology') ?? [],
			},
		}))
	}, [searchParams])

	const container = {
		hidden: { opacity: 0 },
		show: {
			opacity: 1,

			transition: {
				duration: 0.3,
				staggerChildren: 0.2,
			},
		},
	}

	const listItem = {
		hidden: { opacity: 0 },
		show: { opacity: 1 },
	}

	return (
		<Sheet>
			<SheetTrigger asChild>
				<div className="-ml-xxs flex flex-wrap items-center justify-center gap-x-xxxs gap-y-0 sm:justify-end md:ml-0 lg:gap-x-sm">
					<ProductFilterButton
						onClick={() => {
							setSelected((prev) => ({ ...prev, selected: 'color' }))
						}}
					>
						{t('public.filters.title')}
					</ProductFilterButton>

					{/* We comment these out because of the latest design requiring just a "filters" button instead of all the options layed out. We maintain it because of the changing nature of these guys. */}
					{/*<ProductFilterButton*/}
					{/*	onClick={() => {*/}
					{/*		setSelected((prev) => ({ ...prev, selected: 'color' }))*/}
					{/*	}}*/}
					{/*>*/}
					{/*	{t('public.filters.color')}*/}
					{/*</ProductFilterButton>*/}
					{/*<ProductFilterButton*/}
					{/*	onClick={() => {*/}
					{/*		setSelected((prev) => ({ ...prev, selected: 'typology' }))*/}
					{/*	}}*/}
					{/*>*/}
					{/*	{t('public.filters.typology')}*/}
					{/*</ProductFilterButton>*/}
					{/*<ProductFilterButton*/}
					{/*	onClick={() => {*/}
					{/*		setSelected((prev) => ({ ...prev, selected: 'width' }))*/}
					{/*	}}*/}
					{/*>*/}
					{/*	{t('public.filters.width')}*/}
					{/*</ProductFilterButton>*/}
					{/*<ProductFilterButton*/}
					{/*	onClick={() => {*/}
					{/*		setSelected((prev) => ({ ...prev, selected: 'finish' }))*/}
					{/*	}}*/}
					{/*>*/}
					{/*	{t('public.filters.finish')}*/}
					{/*</ProductFilterButton>*/}
					{/*<ProductFilterButton*/}
					{/*	onClick={() => {*/}
					{/*		setSelected((prev) => ({ ...prev, selected: 'technology' }))*/}
					{/*	}}*/}
					{/*>*/}
					{/*	{t('public.filters.technology')}*/}
					{/*</ProductFilterButton>*/}
				</div>
			</SheetTrigger>
			<SheetContent
				showOverlay={false}
				className="flex flex-col justify-between gap-sm"
			>
				<div className="sr-only">
					<SheetHeader>
						<SheetTitle>{t('public.filters.filterBy')}</SheetTitle>
					</SheetHeader>
				</div>
				<div />
				<div
					className={cn(
						'flex gap-md px-sm md:gap-xl',
						'select-none transition-all duration-200 ease-out',
						'text-sm font-medium uppercase tracking-[0.2em] text-brand-dusk',
					)}
				>
					{/* Main menu, left side items (first level) */}
					<ul className="flex flex-col gap-md">
						{Object.entries(filterItems)
							// Remove technology filter because it contains only one option and they want it to be on the first level.
							.filter(([key]) => key !== 'technology')
							.map(([key]) => (
								<li key={key}>
									<button
										className={cn(
											'text-left font-light uppercase',
											selected.selected === key && 'font-semibold',
										)}
										type="button"
										onClick={() => {
											setSelected((prev) => ({
												...prev,
												selected: key as FilterKey,
											}))
										}}
									>
										{t(`public.filters.${key}`)}.{' '}
										{selected.activeFilters[key]
											? selected.activeFilters[key].length > 0
												? `(${selected.activeFilters[key].length})`
												: ''
											: ''}
									</button>
								</li>
							))}
						<li>
							<button
								className={cn(
									'text-left font-light uppercase',
									selected.activeFilters?.technology?.includes('4d') &&
										'font-semibold',
								)}
								type="button"
								onClick={() => {
									setSelected((prev) => ({
										...prev,
										activeFilters: {
											...selected.activeFilters,
											technology: selected.activeFilters?.technology?.includes(
												'4d',
											)
												? []
												: ['4d'],
										},
									}))
								}}
							>
								4D.
							</button>
						</li>
					</ul>
					{/* Submenu, right side items (second level) */}
					{selected.selected && (
						<motion.ul
							className="flex flex-col gap-md"
							variants={container}
							initial="hidden"
							animate="show"
						>
							{Object.entries(
								// @ts-ignore
								filterItems[selected.selected],
							).map(([key, value]) => (
								<motion.li key={key} variants={listItem}>
									<button
										className={cn(
											'text-left font-light uppercase',
											selected.activeFilters[selected.selected]?.includes(
												key,
											) && 'font-semibold',
										)}
										onClick={() => {
											setSelected((prev) => {
												const activeFilters = {
													...prev.activeFilters,
													[selected.selected]:
														(prev.activeFilters[selected.selected]?.includes(
															key,
														)
															? prev.activeFilters[selected.selected]?.filter(
																	(v) => v !== key,
																)
															: [
																	...(prev.activeFilters[selected.selected] ??
																		[]),
																	key,
																]) ?? [],
												}

												return {
													...prev,
													activeFilters,
												}
											})
										}}
									>
										{value}
									</button>
								</motion.li>
							))}
						</motion.ul>
					)}
				</div>
				<div />
				<div className="absolute bottom-7 right-7 flex items-center">
					{searchParams.size > 0 && (
						<ProductFilterButton
							onClick={() => {
								setSearchParams(new URLSearchParams(), {
									viewTransition: true,
								})
								setSelected((prev) => ({
									...prev,
									activeFilters: {},
								}))
							}}
							className="!text-status-danger"
						>
							{t('public.filters.actions.reset')}
						</ProductFilterButton>
					)}
					<Button
						variant="link"
						// If all filters are the same as the selected ones, disable the button
						disabled={Object.entries(selected.activeFilters).every(
							([key, value]) =>
								searchParams.getAll(key).join() === value.join(),
						)}
						className="text-base lowercase"
						onClick={() => {
							const params = new URLSearchParams()

							if (searchParams.has('search')) {
								params.set('search', searchParams.get('search') as string)
							}

							Object.entries(selected.activeFilters).forEach(([key, value]) => {
								value.forEach((v) => {
									params.append(key, v)
								})
							})

							setSearchParams(params, {
								viewTransition: true,
							})
						}}
					>
						{t('public.filters.actions.apply')}
					</Button>
				</div>
			</SheetContent>
		</Sheet>
	)
}

export default function ProductsLayout({ loaderData }: Route.ComponentProps) {
	const { t } = useTranslation('products')
	const [searchParams, setSearchParams] = useSearchParams()
	const [searchInput, setSearchInput] = useState('' as string)
	const collectionsContainer = useRef<HTMLDivElement>(null)
	const [columns, setColumns] = useState(2)

	const {
		navigation: { route },
	} = usePublicContext()

	useEffect(() => {
		route.setContent([
			{
				label: `${t('title')}.`,
			},
		])
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		const search = searchParams.get('search') ?? ''
		setSearchInput(search)
	}, [searchParams])

	useEffect(() => {
		const container = collectionsContainer.current
		if (!container) return

		const resizeObserver = new ResizeObserver(() => {
			const rect = container.getBoundingClientRect()
			const amount = Math.floor(rect.width / (150 + 16))
			setColumns(amount)
		})

		resizeObserver.observe(container)

		return () => {
			resizeObserver.disconnect()
		}
	}, [])

	// Not needed since we suspended the search input.

	// const handleSearchQuery = (searchValue: string) => {
	// 	const params = searchParams
	//
	// 	if (searchValue) {
	// 		params.set('search', searchValue)
	// 	} else {
	// 		params.delete('search')
	// 	}
	//
	// 	setSearchParams(params)
	// }

	// const debouncedSearch = useDebounce((searchValue: string) => {
	// 	handleSearchQuery(searchValue)
	// }, 500)

	const filteredCollections = loaderData?.collections.filter((collection) =>
		collection.name.toLowerCase().includes(searchInput.toLowerCase()),
	)

	if (!loaderData) return null

	const { typologies, filters, amountOfCollections } = loaderData

	return (
		<>
			<div>
				<MarketingSpacer side="top" />
				<div className="px-sm sm:container md:max-w-6xl">
					<div className="flex flex-col justify-center gap-x-sm sm:justify-between md:flex-row">
						<div className="flex w-full flex-col-reverse items-center gap-xxs sm:flex-row md:w-auto">
							{/*<div className="flex w-full items-center gap-xxs sm:w-auto">*/}
							{/*	<Icon name="magnifying-glass" className="" />*/}
							{/*	<input*/}
							{/*		value={searchInput}*/}
							{/*		onChange={(e) => {*/}
							{/*			const searchValue = e.target.value*/}

							{/*			// Component state*/}
							{/*			setSearchInput(searchValue)*/}
							{/*			// Search params state*/}
							{/*			debouncedSearch(searchValue)*/}
							{/*		}}*/}
							{/*		type="text"*/}
							{/*		className={cn(*/}
							{/*			'w-full truncate border-b border-input px-xxxs py-xxxs text-sm font-light tracking-[0.05em] text-brand-dusk outline-none placeholder:text-sm placeholder:font-light placeholder:lowercase sm:w-auto lg:max-w-none',*/}
							{/*			searchParams.size > 0 && 'md:max-w-[100px]',*/}
							{/*		)}*/}
							{/*		placeholder={t('public.filters.search.name')}*/}
							{/*	/>*/}
							{/*	{searchInput && (*/}
							{/*		<Button size="icon" variant="ghost" className="!h-8 !w-8">*/}
							{/*			<Icon*/}
							{/*				name="lucide-x"*/}
							{/*				onClick={() => {*/}
							{/*					setSearchInput('')*/}
							{/*					handleSearchQuery('')*/}
							{/*				}}*/}
							{/*			/>*/}
							{/*		</Button>*/}
							{/*	)}*/}
							{/*</div>*/}
							{searchParams.size > 0 ? (
								<ProductFilterButton
									onClick={() => {
										setSearchParams(new URLSearchParams())
									}}
									className="min-w-fit truncate !text-status-danger"
								>
									{t('public.filters.actions.reset')}
								</ProductFilterButton>
							) : null}
						</div>
						<ProductFilterSheet filterItems={filters} />
					</div>
					<div
						className="mx-auto my-sm flex justify-center md:my-md lg:my-lg xl:my-xl"
						ref={collectionsContainer}
					>
						<div
							style={{
								gridTemplateColumns: `repeat(${columns === 0 ? 'auto-fill' : columns}, 1fr)`,
							}}
							className={cn(
								// "flex w-fit justify-center gap-x-sm gap-y-sm sm:justify-start sm:gap-y-xxl"
								`grid gap-sm sm:gap-y-xl`,
							)}
						>
							{searchParams.size === 0 && (
								<LocalizedLink
									to={routes.public.landings.dressYourLook.route({})}
									className={cn(
										'z-10 col-span-2 max-w-[316px] overflow-hidden',
									)}
									style={{
										gridRowStart: Math.min(filteredCollections.length, 2),
										gridColumnStart: Math.round(
											filteredCollections.length > columns
												? columns / 2
												: filteredCollections.length / 2,
										),
									}}
								>
									<div className={'relative h-[300px] overflow-hidden'}>
										<div
											className={
												'absolute inset-0 z-10 flex select-none flex-col items-center justify-center text-white'
											}
										>
											<Text
												as={'p'}
												size={'h2'}
												className={'-space-y-2 uppercase'}
												weight={'light'}
												type={'serif'}
											>
												<span className={'block'}>DRESS</span>
												<span className={'block'}>CODE</span>
											</Text>
											<p
												className={
													'text-[9px] font-thin uppercase tracking-[0.2em]'
												}
											>
												Jaime Beriestain
											</p>
										</div>
										<Image
											className={'w-full !scale-150 brightness-50'}
											src={
												'/img/homepage/dress-your-look/20250204_Sapienstone_Jaime_0218_OK_CMYK.webp'
											}
											alt={'Dress Code Jaime Beriestain + Sapienstone'}
										/>
									</div>
									<div className="mx-sm mt-sm text-left text-brand-dusk">
										<h2 className="text-[10px] font-semibold uppercase tracking-[0.2em]">
											DRESS CODE <br />
											JAIME BERIESTAIN
										</h2>
									</div>
								</LocalizedLink>
							)}
							{filteredCollections.length > 0 ? (
								filteredCollections.map((collection) => (
									<Link
										viewTransition
										to={routes.public.products.view.route({
											slug: collection.slug,
										})}
										key={collection.id}
										className="flex min-w-[150px] max-w-[150px] flex-col gap-sm"
									>
										<Image
											src={getMaterialDesignImgSrc(
												collection.designsImages[0]?.id ?? '',
											)}
											alt={collection.name}
											className="aspect-auto h-[300px] w-auto"
										/>
										<div className="mx-sm text-left text-brand-dusk">
											<h2 className="text-[10px] font-semibold uppercase tracking-[0.2em]">
												{collection.name}
											</h2>
											{collection.characteristics?.new && (
												<p className="font-serif text-sm lowercase italic text-gray-600">
													{t('public.product.new')}
												</p>
											)}
										</div>
									</Link>
								))
							) : (
								<div className="col-span-full mx-xs">
									{amountOfCollections > 0
										? t('public.products.notFound')
										: t('public.products.noProducts')}
								</div>
							)}
						</div>
					</div>
				</div>
				<MarketingSpacer side="top" />
				<div className="mx-auto flex max-w-xl flex-wrap items-center justify-center gap-xxs px-sm text-center lg:max-w-4xl lg:gap-x-sm">
					{Object.entries(filters.typology).map(([key, value]) => (
						<button
							className={cn(
								'font-serif text-h3 !font-light lowercase lg:text-h1',
								searchParams.get('typology') === key && '!font-semibold',
							)}
							onClick={() => {
								const params = new URLSearchParams(searchParams.toString())

								if (params.get('typology') === key) {
									params.delete('typology')
								} else {
									params.set('typology', key)
								}

								setSearchParams(params)
							}}
							key={key}
						>
							{value as string}.{' '}
							<span className="-ml-xxs align-super text-lg lg:text-2xl">
								(
								{
									typologies.find((typology) => typology.code === key)?._count
										.collections
								}
								)
							</span>
						</button>
					))}
				</div>
				<MarketingSpacer side="bottom" />
			</div>
		</>
	)
}
